import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Tag, X, BadgeCheck } from 'lucide-react';
import { PaymentMethodPicker } from './checkout/PaymentMethodPicker';
import { UpiBlock, BankBlock } from './checkout/PaymentBlocks';

export const Checkout = () => {
  const { cart, cartTotal, clearCart, sessionId, hydrated } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [payment, setPayment] = useState(null);
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null); // {code, discount_percent, festival_name}
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [form, setForm] = useState({
    shipping_address: user?.address || '',
    shipping_city: user?.city || '',
    shipping_state: user?.state || 'West Bengal',
    shipping_pincode: user?.pincode || '',
    payment_method: 'upi',
    payment_reference: '',
    notes: '',
  });

  useEffect(() => {
    api.get('/payment/details').then(r => setPayment(r.data)).catch(() => {});
    // Auto-prefill coupon from the festive banner (copied via FestiveBanner.copyCode)
    const pending = localStorage.getItem('sb_pending_coupon');
    if (pending) setCouponInput(pending);
  }, []);

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) {
      toast.error('Enter a coupon code first');
      return;
    }
    setValidatingCoupon(true);
    try {
      const res = await api.post('/coupons/validate', { code });
      setAppliedCoupon(res.data);
      localStorage.setItem('sb_pending_coupon', res.data.code);
      toast.success(`Coupon applied — ${res.data.discount_percent}% off!`);
    } catch (err) {
      setAppliedCoupon(null);
      toast.error(err.response?.data?.detail || 'Invalid coupon');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    localStorage.removeItem('sb_pending_coupon');
  };

  // Money math (must mirror backend so UI matches the receipt)
  const subtotal = cartTotal;
  const discountAmount = appliedCoupon
    ? Math.round(subtotal * (appliedCoupon.discount_percent / 100) * 100) / 100
    : 0;
  const discountedSubtotal = subtotal - discountAmount;
  const shipping = discountedSubtotal >= 500 ? 0 : 50;
  const tax = Math.round(discountedSubtotal * 0.05 * 100) / 100;
  const total = Math.round((discountedSubtotal + shipping + tax) * 100) / 100;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.shipping_address || !form.shipping_city || !form.shipping_pincode) {
      toast.error('Fill all shipping details');
      return;
    }
    if ((form.payment_method === 'upi' || form.payment_method === 'bank') && !form.payment_reference.trim()) {
      toast.error('Please enter payment reference / transaction ID');
      return;
    }
    setLoading(true);
    try {
      await api.post('/orders', {
        items: cart,
        session_id: sessionId,
        coupon_code: appliedCoupon?.code || undefined,
        ...form,
      });
      clearCart();
      localStorage.removeItem('sb_pending_coupon');
      toast.success('Order placed!');
      navigate('/orders');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hydrated && cart.length === 0) navigate('/cart');
  }, [hydrated, cart.length, navigate]);

  if (!hydrated) {
    return <div className="max-w-5xl mx-auto px-4 py-16 text-center text-gray-500">Loading...</div>;
  }
  if (cart.length === 0) {
    return null;
  }

  const pm = form.payment_method;
  const needsRef = pm === 'upi' || pm === 'bank';  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="checkout-page">
      <h1 className="text-3xl font-bold text-orange-800 mb-8">Checkout</h1>
      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-bold mb-4 text-orange-800">Shipping Address</h3>
              <div className="grid gap-4">
                <div>
                  <Label>Address *</Label>
                  <Input data-testid="shipping-address" value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>City *</Label><Input data-testid="shipping-city" value={form.shipping_city} onChange={(e) => setForm({ ...form, shipping_city: e.target.value })} required /></div>
                  <div><Label>State *</Label><Input data-testid="shipping-state" value={form.shipping_state} onChange={(e) => setForm({ ...form, shipping_state: e.target.value })} required /></div>
                </div>
                <div><Label>Pincode *</Label><Input data-testid="shipping-pincode" value={form.shipping_pincode} onChange={(e) => setForm({ ...form, shipping_pincode: e.target.value })} required /></div>
                <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-bold mb-4 text-orange-800">Payment Method</h3>
              <PaymentMethodPicker value={pm} onChange={(v) => setForm({ ...form, payment_method: v })} />

              {pm === 'upi' && payment && <UpiBlock payment={payment} total={total} />}
              {pm === 'bank' && payment && <BankBlock payment={payment} total={total} />}

              {needsRef && (
                <div className="mt-4">
                  <Label>Payment Reference / Transaction ID *</Label>
                  <Input
                    data-testid="payment-reference"
                    placeholder={pm === 'upi' ? 'e.g. 412345678901' : 'e.g. UTR or NEFT ref number'}
                    value={form.payment_reference}
                    onChange={(e) => setForm({ ...form, payment_reference: e.target.value })}
                    required
                  />
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="bg-white rounded-xl p-6 shadow-sm sticky top-24">
              <h3 className="font-bold mb-4 text-orange-800">Order Summary</h3>
              <div className="text-sm space-y-2 max-h-48 overflow-y-auto">
                {cart.map(i => (
                  <div key={i.product_id} className="flex justify-between">
                    <span className="truncate pr-2">{i.product_name} x{i.quantity}</span>
                    <span className="whitespace-nowrap">₹{i.price * i.quantity}</span>
                  </div>
                ))}
              </div>

              {/* Coupon code */}
              <div className="mt-4 pt-4 border-t" data-testid="coupon-section">
                {!appliedCoupon ? (
                  <div>
                    <Label className="flex items-center gap-1 text-xs text-gray-600">
                      <Tag size={12} /> Have a discount code?
                    </Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        data-testid="coupon-input"
                        placeholder="e.g. SPRING12"
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                        className="font-mono uppercase text-sm"
                      />
                      <Button
                        type="button"
                        data-testid="coupon-apply-btn"
                        onClick={applyCoupon}
                        disabled={validatingCoupon || !couponInput.trim()}
                        variant="outline"
                        className="border-orange-600 text-orange-700 hover:bg-orange-50 shrink-0"
                      >
                        {validatingCoupon ? '…' : 'Apply'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    data-testid="coupon-applied"
                    className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
                  >
                    <div className="flex items-center gap-2 text-sm">
                      <BadgeCheck size={16} className="text-emerald-600" />
                      <div>
                        <p className="font-mono font-semibold text-emerald-800">
                          {appliedCoupon.code}
                        </p>
                        <p className="text-xs text-emerald-700">
                          {appliedCoupon.discount_percent}% off · {appliedCoupon.festival_name}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      data-testid="coupon-remove-btn"
                      onClick={removeCoupon}
                      className="text-emerald-700 hover:text-emerald-900 p-1"
                      aria-label="Remove coupon"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>

              <div className="border-t mt-4 pt-4 text-sm space-y-1">
                <div className="flex justify-between"><span>Subtotal</span><span>₹{subtotal}</span></div>
                {appliedCoupon && (
                  <div
                    data-testid="discount-line"
                    className="flex justify-between text-emerald-700 font-medium"
                  >
                    <span>Discount ({appliedCoupon.discount_percent}%)</span>
                    <span>− ₹{discountAmount}</span>
                  </div>
                )}
                <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? 'FREE' : `₹${shipping}`}</span></div>
                <div className="flex justify-between"><span>Tax (5%)</span><span>₹{tax}</span></div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t"><span>Total</span><span data-testid="checkout-total" className="text-orange-600">₹{total}</span></div>
                {appliedCoupon && (
                  <p className="text-xs text-emerald-700 text-right">
                    You're saving ₹{discountAmount}!
                  </p>
                )}
              </div>
              <Button type="submit" disabled={loading} data-testid="place-order-btn" className="w-full mt-4 bg-orange-600 hover:bg-orange-700">
                {loading ? 'Processing...' : `Place Order - ₹${total}`}
              </Button>
              <p className="text-xs text-center text-gray-500 mt-2">Your order will be confirmed after we verify the payment.</p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
